import axios, { type AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import { getAuthToken } from '../utils/cookies';
import { notifyIfInvalidTokenResponse } from '../utils/authExpiredHandler';

/**
 * Axios instance for authenticated API calls. Automatically adds
 * Authorization: Bearer <token> from cookies to every request.
 */
export function createAuthClient(): AxiosInstance {
  const client = axios.create({
    baseURL: process.env.REACT_APP_API_URL,
  });

  client.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    const token = getAuthToken();
    if (!token) return config;
    const nextConfig = { ...config };
    nextConfig.headers.set('Authorization', `Bearer ${token}`);
    return nextConfig;
  });

  client.interceptors.response.use(
    (res) => res,
    (error: AxiosError<unknown>) => {
      const status = error.response?.status;
      if (status != null) {
        notifyIfInvalidTokenResponse(status, error.response?.data);
      }
      return Promise.reject(error);
    }
  );

  return client;
}

/** Pre-configured client for authenticated requests. Use this for protected endpoints. */
export const authClient = createAuthClient();
